import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { leadsApi, appointmentsApi, paymentsApi, patientsApi, patientAttendanceApi, staffApi, therapyApi } from '../../services/api';
import TodaysBirthdaysCard from '../../components/Dashboard/TodaysBirthdaysCard';
import StaffOnLeaveTodayCard from '../../components/Dashboard/StaffOnLeaveTodayCard';
import SessionEndingAlertsCard from '../../components/Dashboard/SessionEndingAlertsCard';
import { getCurrentHourOfDayIST, todayYmdIST } from '../../utils/timezoneDisplay';
import {
  HiOutlineUserGroup,
  HiOutlineUsers,
  HiOutlineCalendar,
  HiOutlineCurrencyRupee,
  HiOutlineArrowRight,
  HiOutlinePhone,
  HiOutlineClock
} from 'react-icons/hi';
import { Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const Dashboard = () => {
  const { user } = useAuth();
  const { canRead, canCreate, canViewPricing, isAdmin, isClinicAdmin } = usePermissions();
  const canViewSessionAlerts = isAdmin || isClinicAdmin;
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(() => todayYmdIST());
  const [dateTo, setDateTo] = useState(() => todayYmdIST());
  const [birthdaysLoading, setBirthdaysLoading] = useState(true);
  const [birthdays, setBirthdays] = useState([]);
  const [staffOnLeave, setStaffOnLeave] = useState(null);
  const [staffOnLeaveLoading, setStaffOnLeaveLoading] = useState(false);
  const [sessionAlerts, setSessionAlerts] = useState({ ending_soon: [], ended: [] });
  const [sessionAlertsLoading, setSessionAlertsLoading] = useState(false);
  const [stats, setStats] = useState({
    leads: null,
    appointments: null,
    payments: null,
    patients: null,
    todaysAssumption: null,
    monthlyAssumption: null,
  });

  const statsDateParams = useMemo(() => {
    if (!isAdmin) return null;
    return { date_from: dateFrom, date_to: dateTo };
  }, [isAdmin, dateFrom, dateTo]);

  const periodLabel = useMemo(
    () => formatStatsPeriodLabel(dateFrom, dateTo),
    [dateFrom, dateTo]
  );

  const isSingleDayPeriod = dateFrom === dateTo;

  const startCallingHref = useMemo(() => {
    const leadId = stats.leads?.nextFollowUpLeadId;
    if (leadId) return `/leads/${leadId}`;
    return '/leads?filter=follow_up_pending';
  }, [stats.leads?.nextFollowUpLeadId]);

  const fetchDashboardData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const rangeParams = statsDateParams || undefined;
      const jobs = [];
      if (canRead('leads')) jobs.push({ key: 'leads', run: () => leadsApi.getStats(rangeParams) });
      if (canRead('appointments')) jobs.push({ key: 'appointments', run: () => appointmentsApi.getStats(rangeParams) });
      if (canRead('payments') && canViewPricing) jobs.push({ key: 'payments', run: () => paymentsApi.getStats(rangeParams) });
      if (canRead('patients')) jobs.push({ key: 'patients', run: () => patientsApi.getStats(rangeParams) });
      if (canRead('attendance') && canViewPricing) {
        jobs.push({
          key: 'todaysAssumption',
          run: () => patientAttendanceApi.getTodaysAssumptionAmount(rangeParams),
        });
        jobs.push({ key: 'monthlyAssumption', run: () => patientAttendanceApi.getTotalAssumptionAmount() });
      }

      const settled = await Promise.allSettled(jobs.map((j) => j.run()));
      const next = {
        leads: null,
        appointments: null,
        payments: null,
        patients: null,
        todaysAssumption: null,
        monthlyAssumption: null,
      };
      settled.forEach((s, i) => {
        const key = jobs[i].key;
        if (s.status === 'fulfilled' && s.value?.data?.data !== undefined) {
          next[key] = s.value.data.data;
        }
      });
      setStats(next);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [user, canRead, canViewPricing, statsDateParams]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const fetchBirthdays = useCallback(async () => {
    setBirthdaysLoading(true);
    try {
      const res = await patientsApi.getTodaysBirthdays({ sort: 'name' });
      setBirthdays(res.data?.data?.birthdays || []);
    } catch (e) {
      console.error('Birthdays fetch:', e);
      setBirthdays([]);
    } finally {
      setBirthdaysLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBirthdays();
  }, [fetchBirthdays]);

  const fetchSessionAlerts = useCallback(async () => {
    if (!canViewSessionAlerts) {
      setSessionAlerts({ ending_soon: [], ended: [] });
      return;
    }
    setSessionAlertsLoading(true);
    try {
      const res = await therapyApi.getEndingAlerts();
      setSessionAlerts({
        ending_soon: res.data?.data?.ending_soon || [],
        ended: res.data?.data?.ended || [],
      });
    } catch (e) {
      console.error('Session alerts fetch:', e);
      setSessionAlerts({ ending_soon: [], ended: [] });
    } finally {
      setSessionAlertsLoading(false);
    }
  }, [canViewSessionAlerts]);

  useEffect(() => {
    fetchSessionAlerts();
  }, [fetchSessionAlerts]);

  const fetchStaffOnLeave = useCallback(async () => {
    if (!canRead('staff')) {
      setStaffOnLeave(null);
      return;
    }
    setStaffOnLeaveLoading(true);
    try {
      const res = await staffApi.getOnLeaveToday();
      if (res.data?.success) {
        setStaffOnLeave(res.data.data);
      }
    } catch (e) {
      console.error('Staff on leave fetch:', e);
      setStaffOnLeave(null);
    } finally {
      setStaffOnLeaveLoading(false);
    }
  }, [canRead]);

  useEffect(() => {
    fetchStaffOnLeave();
    const onAttendanceChange = () => fetchStaffOnLeave();
    window.addEventListener('staffAttendanceChanged', onAttendanceChange);
    return () => window.removeEventListener('staffAttendanceChanged', onAttendanceChange);
  }, [fetchStaffOnLeave]);

  const useAdminRange = isAdmin && statsDateParams;

  const statCardsAll = useMemo(
    () => [
      {
        title: useAdminRange ? 'Leads' : 'Total Leads',
        value: stats.leads?.todayLeads || 0,
        subtitle: useAdminRange ? periodLabel : 'Today',
        icon: HiOutlineUserGroup,
        color: 'bg-blue-500',
        href: '/leads',
        module: 'leads',
      },
      {
        title: 'Active Patients',
        value: stats.patients?.totalActive || 0,
        subtitle: useAdminRange
          ? `+${stats.patients?.newThisMonth || 0} enrolled · ${periodLabel}`
          : `+${stats.patients?.newThisMonth || 0} this month`,
        icon: HiOutlineUsers,
        color: 'bg-emerald-500',
        href: '/patients',
        module: 'patients',
      },
      {
        title: useAdminRange ? 'Appointments' : "Today's Appointments",
        value: stats.appointments?.today?.total || 0,
        subtitle: `${stats.appointments?.today?.completed || 0} completed · ${useAdminRange ? periodLabel : 'today'}`,
        icon: HiOutlineCalendar,
        color: 'bg-amber-500',
        href: '/calendar',
        module: 'appointments',
      },
      ...(canViewPricing ? [{
        title: useAdminRange ? 'Collection' : "Today's Collection",
        value: formatAmount(parseFloat(stats.payments?.todayReceived) || 0),
        subtitle: useAdminRange
          ? `${formatAmount(parseFloat(stats.payments?.todayPending) || 0)} pending · ${periodLabel}`
          : `${formatAmount(parseFloat(stats.payments?.todayPending) || 0)} remaining today`,
        icon: HiOutlineCurrencyRupee,
        color: 'bg-purple-500',
        href: '/payments',
        module: 'payments',
      },
      {
        title: useAdminRange ? 'Invoice balance' : 'Monthly Collection',
        value: formatAmount(
          parseFloat(
            useAdminRange ? stats.payments?.monthlyPending : stats.payments?.monthlyReceived
          ) || 0
        ),
        subtitle: useAdminRange
          ? `Outstanding in period · ${periodLabel}`
          : `${formatAmount(parseFloat(stats.payments?.monthlyPending) || 0)} remaining this month`,
        icon: HiOutlineCurrencyRupee,
        color: 'bg-indigo-500',
        href: '/payments',
        module: 'payments',
      },
      {
        title: useAdminRange && !isSingleDayPeriod ? 'Present Amount' : "Today's Present Amount",
        value: parseFloat(stats.todaysAssumption?.presentAmount) || 0,
        subtitle: useAdminRange && !isSingleDayPeriod
          ? `${stats.todaysAssumption?.presentCount || 0} present days · ${stats.todaysAssumption?.absentCount || 0} absent · ${periodLabel}`
          : `${stats.todaysAssumption?.presentCount || 0} present · ${stats.todaysAssumption?.absentCount || 0} absent · ${stats.todaysAssumption?.unmarkedCount || 0} scheduled unmarked`,
        icon: HiOutlineCurrencyRupee,
        color: 'bg-green-500',
        href: '/patient-attendance',
        showFullAmount: true,
        module: 'attendance',
      },
      {
        title: 'Monthly Present Amount',
        value: parseFloat(stats.monthlyAssumption?.totalPresentDaysAmount) || 0,
        subtitle: `${stats.monthlyAssumption?.patientCount || 0} present patients`,
        icon: HiOutlineCurrencyRupee,
        color: 'bg-teal-500',
        href: '/patient-attendance',
        showFullAmount: true,
        module: 'attendance',
      }] : []),
    ],
    [stats, useAdminRange, periodLabel, isSingleDayPeriod, canViewPricing]
  );

  const statCards = useMemo(
    () => statCardsAll.filter((c) => !c.module || canRead(c.module)),
    [statCardsAll, canRead]
  );

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#6B7280', '#8B5CF6'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-3 md:space-y-6">
      {/* Welcome — compact on phones, unchanged rhythm on md+ */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
        <div className="min-w-0">
          <h1 className="font-display text-lg font-bold leading-tight text-gray-900 md:text-2xl">
            Good {getGreeting()}, {user?.full_name?.split(' ')[0]}
            <span className="ml-0.5" aria-hidden>
              👋
            </span>
          </h1>
          <p className="mt-0.5 text-xs text-gray-500 md:mt-1 md:text-base">
            Here&apos;s what&apos;s happening at your clinic today.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isAdmin && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white px-2 py-1.5">
              <span className="text-xs font-medium text-gray-500 whitespace-nowrap">Date range</span>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  const v = e.target.value;
                  setDateFrom(v);
                  if (v && dateTo && v > dateTo) setDateTo(v);
                }}
                className="rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                aria-label="From date"
              />
              <span className="text-xs text-gray-400">to</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  const v = e.target.value;
                  setDateTo(v);
                  if (v && dateFrom && v < dateFrom) setDateFrom(v);
                }}
                min={dateFrom || undefined}
                className="rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                aria-label="To date"
              />
            </div>
          )}
          {canCreate('leads') && (
            <Link to="/leads" className="btn btn-secondary min-h-10 justify-center px-3 py-2 text-sm md:min-h-[44px] md:px-4">
              <HiOutlineUserGroup className="mr-2 h-4 w-4" />
              New Lead
            </Link>
          )}
          {canCreate('appointments') && (
            <Link to="/calendar" className="btn btn-primary min-h-10 justify-center px-3 py-2 text-sm md:min-h-[44px] md:px-4">
              <HiOutlineCalendar className="mr-2 h-4 w-4" />
              Schedule
            </Link>
          )}
        </div>
      </div>

      {/* Stats grid */}
      {statCards.length === 0 ? (
        <div className="card rounded-xl p-4 text-center text-sm text-gray-600 md:p-6">
          No dashboard metrics are available for your role. Contact an administrator if you need access to
          leads, patients, appointments, payments, or attendance.
        </div>
      ) : (
      <div className="grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 2xl:grid-cols-7">
        {statCards.map((stat) => (
          <Link
            key={stat.title}
            to={stat.href}
            className="card group flex min-h-0 flex-col rounded-xl border-gray-200 p-3 transition-shadow hover:shadow-md md:min-h-[7.5rem] md:p-5"
          >
            <div className="flex flex-1 items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-xs text-gray-500 md:text-sm">{stat.title}</p>
                {stat.showBothAmounts ? (
                  <div className="mt-1 space-y-1">
                    <div>
                      <p className="mb-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-400">Assumption</p>
                      <p className="text-xs font-bold leading-tight text-gray-900">
                        ₹{(stat.value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                    <div className="border-t border-gray-100 pt-1">
                      <p className="mb-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-400">Actual</p>
                      <p className="text-xs font-bold leading-tight text-green-600">
                        ₹{(stat.presentDaysAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
                ) : stat.showFullAmount ? (
                  <p className="mt-0.5 text-base font-bold text-gray-900 md:text-lg">
                    ₹{(stat.value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                  </p>
                ) : (
                  <p className="mt-0.5 text-2xl font-bold text-gray-900 md:mt-1">{stat.value}</p>
                )}
                <p className="mt-0.5 text-[11px] text-gray-500 md:mt-1.5 md:text-xs">{stat.subtitle}</p>
              </div>
              <div className={`${stat.color} shrink-0 rounded-lg p-2 md:p-2.5`}>
                <stat.icon className="h-4 w-4 text-white md:h-5 md:w-5" />
              </div>
            </div>
            <div className="mt-2 flex items-center border-t border-gray-100 pt-2 text-xs font-medium text-primary-600 md:mt-auto md:pt-3 md:text-sm">
              View details
              <HiOutlineArrowRight className="ml-1 h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 md:h-4 md:w-4" />
            </div>
          </Link>
        ))}
      </div>
      )}

      {canRead('patients') && (
        <TodaysBirthdaysCard
          birthdays={birthdays}
          loading={birthdaysLoading}
          count={birthdays.length}
        />
      )}

      {canViewSessionAlerts && (
        <SessionEndingAlertsCard
          endingSoon={sessionAlerts.ending_soon}
          ended={sessionAlerts.ended}
          loading={sessionAlertsLoading}
        />
      )}

      {(canRead('leads') || canRead('appointments')) && (
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3 lg:gap-6">
        {/* Lead status distribution */}
        {canRead('leads') && (
        <div className="card rounded-xl">
          <h3 className="font-semibold text-gray-900 mb-4">Lead Status Distribution</h3>
          <div className="h-64">
            {stats.leads?.byStatus && (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={stats.leads.byStatus.filter(s => s.count > 0)}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="count"
                    nameKey="name"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {stats.leads.byStatus.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
        )}

        {/* Pending follow-ups */}
        <div className={`card rounded-xl ${canRead('leads') ? 'lg:col-span-2' : 'lg:col-span-3'}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Pending Follow-ups</h3>
            {canRead('leads') && (
            <Link to="/leads?filter=follow_up_pending" className="text-sm text-primary-600 hover:text-primary-700">
              View all
            </Link>
            )}
          </div>
          
          {canRead('leads') && stats.leads?.pendingFollowUps > 0 ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between p-4 bg-amber-50 rounded-xl border border-amber-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <HiOutlinePhone className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {stats.leads.pendingFollowUps} leads need follow-up
                    </p>
                    <p className="text-sm text-gray-500">
                      {stats.leads.nextFollowUpLeadName
                        ? `Next: ${stats.leads.nextFollowUpLeadName}`
                        : 'Call or message today'}
                    </p>
                  </div>
                </div>
                <Link to={startCallingHref} className="btn btn-secondary text-sm">
                  Start calling
                </Link>
              </div>
            </div>
          ) : canRead('leads') ? (
            <div className="text-center py-8 text-gray-500">
              <HiOutlineClock className="w-12 h-12 mx-auto text-gray-300 mb-2" />
              <p>No pending follow-ups</p>
            </div>
          ) : null}

          {/* Quick stats */}
          {(canRead('leads') || canRead('appointments')) && (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {canRead('leads') && (
            <div className="p-4 bg-gray-50 rounded-xl">
              <p className="text-sm text-gray-500">Conversion Rate</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.leads?.conversionRate || 0}%
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {isAdmin && statsDateParams ? periodLabel : 'Last 30 days'}
              </p>
            </div>
            )}
            {canRead('appointments') && (
            <div className="p-4 bg-gray-50 rounded-xl">
              <p className="text-sm text-gray-500">Pending Reminders</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.appointments?.pendingReminders || 0}
              </p>
              <p className="text-xs text-gray-500 mt-1">For tomorrow</p>
            </div>
            )}
          </div>
          )}
        </div>
      </div>
      )}

      {canRead('staff') && (
        <StaffOnLeaveTodayCard data={staffOnLeave} loading={staffOnLeaveLoading} />
      )}
    </div>
  );
};

function getGreeting() {
  const hour = getCurrentHourOfDayIST();
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

function formatStatsPeriodLabel(from, to) {
  if (!from || !to) return '';
  if (from === to) {
    if (from === todayYmdIST()) return 'Today';
    try {
      return format(new Date(`${from}T12:00:00`), 'd MMM yyyy');
    } catch {
      return from;
    }
  }
  try {
    const a = format(new Date(`${from}T12:00:00`), 'd MMM yyyy');
    const b = format(new Date(`${to}T12:00:00`), 'd MMM yyyy');
    return `${a} – ${b}`;
  } catch {
    return `${from} – ${to}`;
  }
}

function formatAmount(amount) {
  if (amount >= 100000) {
    return `₹${(amount / 100000).toFixed(1)}L`;
  } else if (amount >= 1000) {
    return `₹${(amount / 1000).toFixed(1)}K`;
  } else {
    return `₹${amount.toLocaleString()}`;
  }
}

export default Dashboard;

